import {
  CountryCode,
  Checkbox,
  Dropdown,
  FlagComponent,
  IconButton,
  Icons,
} from '@commenda/kakashi';
import { IBusinessEntity } from '@commenda/types';
import { cn } from '@commenda/utils';
import { ObligationRulesCategory } from '@prisma/client';
import { Country as CountryPkg } from 'country-state-city';
import { useRef } from 'react';
import { useScrollGradient } from '../../../hooks/useScrollGradient';
import {
  CALENDAR_BG_HOVER,
  CALENDAR_DIVIDER,
  CALENDAR_TEXT_PLACEHOLDER,
  CALENDAR_TEXT_PRIMARY,
  CATEGORY_CHECKBOX_CLASS,
  CATEGORY_LABEL,
} from './calendar.constants';

const ALL_CATEGORIES: ObligationRulesCategory[] = [
  ObligationRulesCategory.DIRECT_TAX,
  ObligationRulesCategory.INDIRECT_TAX,
  ObligationRulesCategory.TRANSFER_PRICING,
  ObligationRulesCategory.ENTITY_COMPLIANCE,
  ObligationRulesCategory.PAYROLL_AND_EMPLOYMENT,
  ObligationRulesCategory.CUSTOMS_AND_TRADE,
];

interface CalendarSidebarProps {
  entities: IBusinessEntity[];
  visibilityMap: Map<number, boolean>;
  enabledCategories: Set<ObligationRulesCategory>;
  onToggleEntity: (entityId: number) => void;
  onToggleCategory: (category: ObligationRulesCategory) => void;
  onSelectAllEntities: () => void;
  onClearEntities: () => void;
}

const CalendarSidebar = ({
  entities,
  visibilityMap,
  enabledCategories,
  onToggleEntity,
  onToggleCategory,
  onSelectAllEntities,
  onClearEntities,
}: CalendarSidebarProps) => {
  const entityScrollRef = useRef<HTMLDivElement>(null);
  const { showTopGradient, showBottomGradient } = useScrollGradient(
    entityScrollRef,
    entities.length
  );

  const allEntitiesVisible = entities.every(
    (entity) => visibilityMap.get(entity.id) !== false
  );
  const noEntitiesVisible = entities.every(
    (entity) => visibilityMap.get(entity.id) === false
  );

  return (
    <div
      className="shrink-0 flex flex-col w-[250px] border-l"
      style={{ borderColor: CALENDAR_DIVIDER }}
      data-ai-section="Calendar filters"
    >
      <div className="shrink-0 flex flex-col gap-2 p-3">
        <div className="flex items-center">
          <span
            className="txt-extra-small-bold"
            style={{ color: CALENDAR_TEXT_PRIMARY }}
          >
            My Calendars
          </span>
        </div>

        <div className="flex flex-col gap-1">
          {ALL_CATEGORIES.map((category) => {
            const checked = enabledCategories.has(category);
            const checkboxColorClass = CATEGORY_CHECKBOX_CLASS[category] ?? '';

            return (
              <button
                key={category}
                type="button"
                className="flex items-center gap-1.5 p-1 rounded-xs w-full text-left hover:bg-[var(--calendar-hover)]"
                style={{ ['--calendar-hover' as string]: CALENDAR_BG_HOVER }}
                onClick={() => onToggleCategory(category)}
              >
                <Checkbox
                  checked={checked}
                  className={checkboxColorClass}
                  // Toggling is handled by the wrapping button; suppress duplicate fire
                  onClick={(e) => e.stopPropagation()}
                  onCheckedChange={() => onToggleCategory(category)}
                />
                <span
                  className="flex-1 truncate txt-extra-small-medium"
                  style={{ color: CALENDAR_TEXT_PRIMARY }}
                >
                  {CATEGORY_LABEL[category] ?? category}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div
        className="shrink-0 border-t"
        style={{ borderColor: CALENDAR_DIVIDER }}
      />

      <div className="flex-1 min-h-0 flex flex-col gap-2 p-3">
        <div className="flex items-center justify-between shrink-0">
          <span
            className="txt-extra-small-bold"
            style={{ color: CALENDAR_TEXT_PRIMARY }}
          >
            My Entities
          </span>
          {entities.length > 0 && (
            <Dropdown
              align="end"
              menu={{
                groups: [
                  {
                    items: [
                      {
                        label: 'Show all',
                        onClick: onSelectAllEntities,
                        disabled: allEntitiesVisible,
                      },
                      {
                        label: 'Hide all',
                        onClick: onClearEntities,
                        disabled: noEntitiesVisible,
                      },
                    ],
                  },
                ],
              }}
            >
              <IconButton
                size="xs"
                className="rounded-xs"
                icon={<Icons.Sliders04 />}
                aria-label="Entity visibility options"
              />
            </Dropdown>
          )}
        </div>

        <div className="relative flex-1 min-h-0">
          <div
            ref={entityScrollRef}
            className="absolute inset-0 flex flex-col overflow-y-auto gap-2"
          >
            {entities.map((entity) => {
              const visible = visibilityMap.get(entity.id) !== false;
              const country =
                entity.generalCorporationInfo.incorporationCountry;
              const name = entity.generalCorporationInfo.legalName;
              const countryName =
                CountryPkg.getCountryByCode(country)?.name ?? country;
              // In-progress incorporations have no legalName yet — fall back
              // to a country-tagged stub. Mirrors EntityCard / EntityPill.
              const isPlaceholderName = !name;
              const displayName =
                name || (country ? `New ${country} Entity` : '');

              return (
                <div
                  key={entity.id}
                  className="flex items-center gap-1.5 pl-1.5 pr-1 py-1 rounded-xs group hover:bg-[var(--calendar-hover)]"
                  style={{ ['--calendar-hover' as string]: CALENDAR_BG_HOVER }}
                >
                  <FlagComponent
                    country={country as CountryCode}
                    countryName={countryName}
                    _className={visible ? '' : 'opacity-50'}
                  />

                  <span
                    className={cn(
                      'flex-1 truncate',
                      isPlaceholderName
                        ? 'txt-extra-small-regular'
                        : 'txt-extra-small-medium'
                    )}
                    style={{
                      color:
                        isPlaceholderName || !visible
                          ? CALENDAR_TEXT_PLACEHOLDER
                          : CALENDAR_TEXT_PRIMARY,
                    }}
                    title={displayName}
                  >
                    {displayName}
                  </span>

                  <div className="shrink-0 flex items-center gap-0.5 opacity-0 group-hover:opacity-100">
                    <IconButton
                      size="xs"
                      icon={visible ? <Icons.Eye /> : <Icons.EyeOff />}
                      aria-label={visible ? `Hide ${name}` : `Show ${name}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        onToggleEntity(entity.id);
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
          {showTopGradient && (
            <div className="pointer-events-none absolute inset-x-0 top-0 h-8 bg-gradient-to-b from-white to-transparent" />
          )}
          {showBottomGradient && (
            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-white to-transparent" />
          )}
        </div>
      </div>
    </div>
  );
};

export default CalendarSidebar;
